/*
 * TFRS Footer — Dark tactical design matching catalogue aesthetic
 * Real contact: Dickinson TX | (832) 225-2838 | info@tx1rs.com
 * SBA badge images displayed prominently
 */

import { Link } from "wouter";
import { Phone, MessageSquareText, Mail, MapPin, Facebook, ExternalLink } from "lucide-react";
import { ResponsiveStorageImage } from "./ResponsiveStorageImage";
import { PHONE_DISPLAY, SMS_PHONE_DISPLAY, SMS_URI, TIPS_SHORT_NAME } from "@shared/companyFacts";

const SBA_BADGE_WIDTH = 64;
const SBA_BADGE_HEIGHT = 80;

const SBA_BADGES = [
  { src: "/manus-storage/WOSBCertified_ddaff520.png", alt: "WOSB Certified", width: SBA_BADGE_WIDTH, height: SBA_BADGE_HEIGHT },
  { src: "/manus-storage/EDWOSBCertified_0a68642e.png", alt: "EDWOSB Certified", width: SBA_BADGE_WIDTH, height: SBA_BADGE_HEIGHT },
  { src: "/manus-storage/SDVOSBCertified_bc6b80e8.png", alt: "SDVOSB Certified", width: SBA_BADGE_WIDTH, height: SBA_BADGE_HEIGHT },
  { src: "/manus-storage/Veteran-OwnedCertified_2cdc5b0e.png", alt: "Veteran-Owned Certified", width: SBA_BADGE_WIDTH, height: SBA_BADGE_HEIGHT },
  { src: "/manus-storage/HUBZoneCertified_88debbc5.png", alt: "HUBZone Certified", width: SBA_BADGE_WIDTH, height: SBA_BADGE_HEIGHT },
  { src: "/manus-storage/8aCertified_a4a3e9cf.png", alt: "8(a) Certified", width: SBA_BADGE_WIDTH, height: SBA_BADGE_HEIGHT },
];

const footerLinks = {
  "Services": [
    { label: "Law Enforcement", href: "/law-enforcement" },
    { label: "Fire & Brush Trucks", href: "/fire-brush-trucks" },
    { label: "K9 Consult", href: "/k9-consult" },
    { label: "Commercial", href: "/commercial" },
    { label: "Municipal Fleet Vehicle Protection", href: "/municipal-fleet-vehicle-protection" },
  ],
  "Company": [
    { label: "Capabilities", href: "/capabilities" },
    { label: "Federal Contracting", href: "/federal-contracting" },
    { label: "Gallery", href: "/gallery" },
    { label: "Resources", href: "/resources" },
    { label: "About Us", href: "/about" },
  ],
  "Contact": [
    { label: "Contact Us", href: "/contact" },
    { label: "Request a Quote", href: "/quote" },
  ],
};

export default function Footer() {
  return (
    <footer style={{ background: "var(--tfrs-navy)" }} className="text-white">
      {/* SBA Certification badges strip */}
      <div className="border-t border-white/10 border-b border-white/10 py-8">
        <div className="container">
          <div className="font-eyebrow text-xs font-bold tracking-[0.3em] text-white/85 text-center uppercase mb-6">
            SBA Certifications &amp; Federal Qualifications
          </div>
          <div className="flex flex-wrap justify-center items-center gap-6">
            {SBA_BADGES.map((badge) => (
              <ResponsiveStorageImage
                key={badge.alt}
                source={badge.src}
                alt={badge.alt}
                title={badge.alt}
                width={badge.width}
                height={badge.height}
                sizes="64px"
                fallbackWidth={96}
                responsiveWidths={[64, 96, 160]}
                loading="lazy"
                decoding="async"
                className="h-20 w-16 object-contain opacity-75 transition-opacity hover:opacity-100"
                style={{ aspectRatio: `${badge.width} / ${badge.height}` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Main footer content */}
      <div className="container py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link href="/" className="inline-block mb-5">
              <span className="font-eyebrow font-black text-sm tracking-widest uppercase text-white leading-tight">
                TEXAS FIRST<br />
                <span className="text-[#c9a84c]">RESPONSE SERVICES</span>
              </span>
            </Link>
            <p className="font-body text-white/85 text-sm leading-relaxed mb-6 max-w-xs">
              Professional vehicle upfitting for law enforcement and fire/EMS. Government-certified contractor serving Texas and beyond. Available through {TIPS_SHORT_NAME} cooperative contracts.
            </p>
            <div className="space-y-3">
              <a href="tel:+18322252838" className="flex items-center gap-2.5 text-white/80 hover:text-white transition-colors font-mono text-sm">
                <Phone size={14} className="text-[var(--tfrs-red-bright)]" />
                Call: {PHONE_DISPLAY}
              </a>
              <a href={SMS_URI} className="flex items-center gap-2.5 text-white/80 hover:text-white transition-colors font-mono text-sm">
                <MessageSquareText size={14} className="text-[var(--tfrs-red-bright)]" />
                Text: {SMS_PHONE_DISPLAY}
              </a>
              <div className="flex items-start gap-2.5 font-body text-sm">
                <Mail size={14} className="text-[var(--tfrs-red-bright)] mt-0.5 flex-shrink-0" />
                <div className="space-y-1.5">
                  <div>
                    <span className="block text-white/85 text-xs font-eyebrow font-bold tracking-widest uppercase">General Inquiries</span>
                    <a href="mailto:info@tx1rs.com" className="text-white/85 hover:text-white transition-colors">info@tx1rs.com</a>
                  </div>
                  <div>
                    <span className="block text-white/85 text-xs font-eyebrow font-bold tracking-widest uppercase">Sales</span>
                    <a href="mailto:sales@tx1rs.com" className="text-white/85 hover:text-white transition-colors">sales@tx1rs.com</a>
                  </div>
                  <div>
                    <span className="block text-white/85 text-xs font-eyebrow font-bold tracking-widest uppercase">Support</span>
                    <a href="mailto:support@tx1rs.com" className="text-white/85 hover:text-white transition-colors">support@tx1rs.com</a>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2.5 text-white/80 font-body text-sm">
                <MapPin size={14} className="text-[var(--tfrs-red-bright)] mt-0.5 flex-shrink-0" />
                Dickinson, TX
              </div>
            </div>
            <div className="flex items-center gap-3 mt-5">
              <a
                href="https://www.facebook.com/TexasFirstResponseServices"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 border border-white/20 hover:border-[#c00a14] hover:bg-[#c00a14]/10 flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)]"
                aria-label="Facebook (opens in a new tab)"
              >
                <Facebook size={16} />
              </a>
              <a
                href="https://tx1rs.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 border border-white/20 hover:border-[#c00a14] hover:bg-[#c00a14]/10 flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)]"
                aria-label="TX1RS.COM (opens in a new tab)"
              >
                <ExternalLink size={14} />
              </a>
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h2 className="font-display text-xs font-bold tracking-widest text-white/85 mb-4 uppercase">
                {category}
              </h2>
              <ul className="space-y-2.5">
                {links.map((link) => (
                  <li key={link.label}>
                    <Link
                      href={link.href}
                      className="font-body text-sm text-white/85 hover:text-white transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--tfrs-navy)]"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Cooperative contracts strip */}
      <div className="border-t border-white/10 py-4">
        <div className="container flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-eyebrow text-xs font-bold tracking-widest text-white/85 uppercase">Available Through:</span>
            {[TIPS_SHORT_NAME].map((contract) => (
              <span key={contract} className="font-eyebrow text-xs font-bold tracking-widest px-3 py-1 border border-white/15 text-white/85 uppercase">
                {contract}
              </span>
            ))}
          </div>
          <div className="flex flex-col items-center gap-2 md:items-end">
            <ul className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
              {[
                { label: "Privacy Policy", href: "/privacy" },
                { label: "Terms of Service", href: "/terms" },
                { label: "Accessibility", href: "/accessibility" },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="font-body inline-block py-1 text-white/85 text-xs underline underline-offset-2 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c]"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="font-body text-white/85 text-xs">
              © {new Date().getFullYear()} Texas First Response Services. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
